// Copied from apps/voip/src/utils/contactSearchIndex.js (origin/master, 2026-10-26) so e-shot
// searches the phone's contacts exactly the way the phone does. The repo rule is
// to copy, never import across apps. Keep in step with the phone's copy.
// Contact name search index - token prefixes + phonetic codes.
//
// Firestore can only prefix-match a single string, so "smith" never found
// "Stephen Smith" (the prefix is on the full name, first name first). We
// store two derived arrays on each voip_contacts doc instead:
//   _nameTokens     - every word of the name plus all its prefixes
//                     ("Stephen Smith" -> st, ste, ..., stephen, sm, smi,
//                     ..., smith), queried with array-contains.
//   _namePhonetics  - a Soundex code per word (Stephen + Steven both
//                     S315), queried with array-contains-any.
//
// KEEP IN SYNC with functions/lib/contact-search-index.js (the CommonJS
// server copy) - the GV sync, attach-contact and backfill paths write the
// same fields. Unit tests in contactSearchIndex.test.js pin behaviour.

const MAX_TOKENS = 6;   // index at most the first 6 words of a name
const MAX_PREFIX = 12;  // prefixes up to 12 chars cover any real search

export function tokeniseName(name) {
  return String(name || '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .slice(0, MAX_TOKENS);
}

// Classic Soundex: first letter + 3 digits. h/w are transparent (letters
// either side with the same code collapse), vowels reset the run.
export function soundex(token) {
  const s = String(token || '').toLowerCase().replace(/[^a-z]/g, '');
  if (!s) return '';
  const codes = {
    b: 1, f: 1, p: 1, v: 1,
    c: 2, g: 2, j: 2, k: 2, q: 2, s: 2, x: 2, z: 2,
    d: 3, t: 3,
    l: 4,
    m: 5, n: 5,
    r: 6,
  };
  const first = s[0].toUpperCase();
  let prev = codes[s[0]] || 0;
  let out = '';
  for (let i = 1; i < s.length && out.length < 3; i++) {
    const ch = s[i];
    if (ch === 'h' || ch === 'w') continue; // transparent - keep prev run
    const code = codes[ch];
    if (code && code !== prev) out += code;
    prev = code || 0;
  }
  return first + out.padEnd(3, '0');
}

export function buildContactNameIndex(name) {
  const tokens = tokeniseName(name);
  const prefixes = new Set();
  const phonetics = new Set();
  const words = new Set(tokens); // exact words only - alias (nickname)
  // lookups query these so "stephen" doesn't pull surname Stephens via
  // its prefix entry in _nameTokens.
  for (const t of tokens) {
    const cap = Math.min(t.length, MAX_PREFIX);
    for (let len = Math.min(2, t.length); len <= cap; len++) {
      prefixes.add(t.slice(0, len));
    }
    if (t.length > MAX_PREFIX) prefixes.add(t); // full long token still matches exactly
    const sx = soundex(t);
    if (sx) phonetics.add(sx);
  }
  return { _nameTokens: [...prefixes], _nameWords: [...words], _namePhonetics: [...phonetics] };
}
